import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { SalesApiService, SalesChannelRecord } from '../sales/sales-api.service';
import {
  InsuranceApiService,
  InsuranceCoverageItem,
  InsurancePremiumBreakdown,
  InsuranceProductListItem,
  InsuranceProductVersionListItem,
  InsuranceRatingPlan
} from './insurance-api.service';

type DetailTab = 'coverageRates' | 'loadings' | 'discounts' | 'taxes' | 'fees' | 'test';
type RuleKind = 'loadings' | 'discounts' | 'taxes' | 'fees';

@Component({
  standalone: true,
  selector: 'ain-insurance-rating-plan-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    CommonModule,
    RouterLink,
    FormsModule,
    TableModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    CustomerSelectorComponent,
    CurrencySelectorComponent,
    InfoHintComponent
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('insurance.rating.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.rating.help.page.title'"
        [descriptionKey]="'insurance.rating.help.page.description'"
        [bulletKeys]="[
          'insurance.rating.help.page.bullets.scope',
          'insurance.rating.help.page.bullets.usage',
          'insurance.rating.help.page.bullets.audit'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/rating']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" (click)="saveGeneral()" [disabled]="saving() || !canSaveGeneral()"></button>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.rating.sections.planEditor') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.rating.help.planEditor.title'"
              [descriptionKey]="'insurance.rating.help.planEditor.description'"
              [bulletKeys]="[
                'insurance.rating.help.planEditor.bullets.mode',
                'insurance.rating.help.planEditor.bullets.floor',
                'insurance.rating.help.planEditor.bullets.adapter'
              ]" />
          </div>
          <div class="grid">
            <label>
              <span>{{ t('insurance.rating.fields.product') }}</span>
              <p-select [options]="productOptions()" optionLabel="label" optionValue="value" [(ngModel)]="selectedProductId" (ngModelChange)="onProductChanged($event)" appendTo="body" />
            </label>
            <label>
              <span>{{ t('insurance.rating.fields.version') }}</span>
              <p-select [options]="versionOptions()" optionLabel="label" optionValue="value" [(ngModel)]="selectedVersionId" (ngModelChange)="onVersionChanged($event)" appendTo="body" />
            </label>
            <label>
              <span>{{ t('insurance.rating.fields.planCode') }}</span>
              <input pInputText [(ngModel)]="planForm.code" />
            </label>
            <label>
              <span>{{ t('insurance.rating.fields.planName') }}</span>
              <input pInputText [(ngModel)]="planForm.name" />
            </label>
            <label>
              <span>{{ t('insurance.rating.fields.currency') }}</span>
              <ain-currency-selector [(ngModel)]="planForm.currencyCode" />
            </label>
            <label>
              <span>{{ t('insurance.rating.fields.mode') }}</span>
              <p-select [options]="modeOptions" optionLabel="label" optionValue="value" [(ngModel)]="planForm.calculationMode" appendTo="body" />
            </label>
            <label>
              <span>{{ t('insurance.rating.fields.externalAdapterKey') }}</span>
              <input pInputText [(ngModel)]="planForm.externalAdapterKey" />
            </label>
            <label>
              <span>{{ t('insurance.rating.fields.minPremiumFloor') }}</span>
              <p-inputNumber [(ngModel)]="rateTableForm.minPremiumFloor" [min]="0" />
            </label>
            <label>
              <span>{{ t('insurance.rating.fields.defaultRatePercent') }}</span>
              <p-inputNumber [(ngModel)]="rateTableForm.defaultRatePercent" [min]="0" [maxFractionDigits]="6" />
            </label>
          </div>
          @if (feedback()) { <p class="hint">{{ feedback() }}</p> }
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'coverageRates'" (click)="activeTab.set('coverageRates')">{{ t('insurance.rating.sections.coverageRates') }}</button>
            <button type="button" [class.active]="activeTab() === 'loadings'" (click)="activeTab.set('loadings')">{{ t('insurance.rating.sections.loadings') }}</button>
            <button type="button" [class.active]="activeTab() === 'discounts'" (click)="activeTab.set('discounts')">{{ t('insurance.rating.sections.discounts') }}</button>
            <button type="button" [class.active]="activeTab() === 'taxes'" (click)="activeTab.set('taxes')">{{ t('insurance.rating.sections.taxes') }}</button>
            <button type="button" [class.active]="activeTab() === 'fees'" (click)="activeTab.set('fees')">{{ t('insurance.rating.sections.fees') }}</button>
            <button type="button" [class.active]="activeTab() === 'test'" (click)="activeTab.set('test')">{{ t('insurance.rating.tabs.test') }}</button>
          </div>

          @if (activeTab() === 'coverageRates') {
            <div class="lines-header">
              <h4>{{ t('insurance.rating.sections.coverageRates') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.rating.help.coverageRates.title'"
                  [descriptionKey]="'insurance.rating.help.coverageRates.description'"
                  [bulletKeys]="[
                    'insurance.rating.help.coverageRates.bullets.base',
                    'insurance.rating.help.coverageRates.bullets.minMax',
                    'insurance.rating.help.coverageRates.bullets.code'
                  ]" />
                <button pButton type="button" [label]="t('common.add')" (click)="openCoverageCreate()"></button>
              </div>
            </div>
            <p-table [value]="rateTableForm.coverageRates">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('insurance.rating.fields.coverageCode') }}</th>
                  <th>{{ t('insurance.rating.fields.baseRatePercent') }}</th>
                  <th>{{ t('insurance.rating.fields.minPremium') }}</th>
                  <th>{{ t('insurance.rating.fields.maxPremium') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row let-i="rowIndex">
                <tr>
                  <td>{{ row.coverageCode }}</td>
                  <td>{{ row.baseRatePercent }}</td>
                  <td>{{ row.minPremium ?? '-' }}</td>
                  <td>{{ row.maxPremium ?? '-' }}</td>
                  <td class="actions-cell">
                    <button pButton type="button" text="true" [label]="t('common.edit')" (click)="openCoverageEdit(i)"></button>
                    <button pButton type="button" text="true" severity="danger" [label]="t('common.delete')" (click)="removeCoverage(i)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (activeTab() === 'loadings') { <ng-container [ngTemplateOutlet]="rulesTable" [ngTemplateOutletContext]="{ kind: 'loadings' }" /> }
          @if (activeTab() === 'discounts') { <ng-container [ngTemplateOutlet]="rulesTable" [ngTemplateOutletContext]="{ kind: 'discounts' }" /> }
          @if (activeTab() === 'taxes') { <ng-container [ngTemplateOutlet]="rulesTable" [ngTemplateOutletContext]="{ kind: 'taxes' }" /> }
          @if (activeTab() === 'fees') { <ng-container [ngTemplateOutlet]="rulesTable" [ngTemplateOutletContext]="{ kind: 'fees' }" /> }

          @if (activeTab() === 'test') {
            <div class="lines-header">
              <h4>{{ t('insurance.rating.tabs.test') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.rating.help.result.title'"
                [descriptionKey]="'insurance.rating.help.result.description'"
                [bulletKeys]="[
                  'insurance.rating.help.result.bullets.base',
                  'insurance.rating.help.result.bullets.adjustments',
                  'insurance.rating.help.result.bullets.total'
                ]" />
            </div>
            <div class="grid">
              <label>
                <span>{{ t('insurance.rating.fields.sumInsured') }}</span>
                <p-inputNumber [(ngModel)]="sumInsured" [min]="0" />
              </label>
              <label>
                <span>{{ t('insurance.rating.fields.factor') }}</span>
                <p-inputNumber [(ngModel)]="factorValue" [min]="0" />
              </label>
            </div>
            <div class="lines-header">
              <h4>{{ t('insurance.rating.testFactors.title') }}</h4>
              <button pButton type="button" [label]="t('common.add')" (click)="addTestFactor()"></button>
            </div>
            @if (testFactors.length === 0) {
              <p class="hint">{{ t('insurance.rating.testFactors.empty') }}</p>
            } @else {
              @for (row of testFactors; track $index) {
                <div class="grid row">
                  <label>
                    <span>{{ t('insurance.rating.fields.factorKey') }}</span>
                    <p-select
                      [options]="factorKeyOptions()"
                      optionLabel="label"
                      optionValue="value"
                      [(ngModel)]="row.factorKey"
                      (ngModelChange)="onTestFactorKeyChanged(row, $event)"
                      appendTo="body" />
                  </label>
                  @if (row.factorKey === 'customer') {
                    <label>
                      <span>{{ t('insurance.rating.fields.customerFactor') }}</span>
                      <ain-customer-selector [(ngModel)]="row.factorRefId" />
                    </label>
                  } @else if (row.factorKey === 'channel') {
                    <label>
                      <span>{{ t('insurance.rating.fields.channelFactor') }}</span>
                      <p-select
                        [options]="channelFactorOptions()"
                        optionLabel="label"
                        optionValue="value"
                        [(ngModel)]="row.factorRefId"
                        [showClear]="true"
                        appendTo="body" />
                    </label>
                  } @else {
                    <label>
                      <span>{{ t('insurance.rating.fields.factorValue') }}</span>
                      <p-inputNumber [(ngModel)]="row.factorValue" [maxFractionDigits]="6" />
                    </label>
                  }
                  <div class="actions-row">
                    <button pButton type="button" severity="danger" [label]="t('common.remove')" (click)="removeTestFactor($index)"></button>
                  </div>
                </div>
              }
            }
            <div class="actions-row">
              <button pButton type="button" [label]="t('insurance.rating.actions.calculate')" (click)="calculate()" [disabled]="!canCalculate()"></button>
              <button pButton type="button" severity="secondary" [label]="t('insurance.rating.actions.reprice')" (click)="reprice()" [disabled]="!canCalculate()"></button>
              <button pButton type="button" severity="secondary" [label]="t('insurance.rating.actions.simulate')" (click)="simulate()" [disabled]="!canCalculate() || !planId()"></button>
            </div>
            @if (breakdown()) {
              <div class="summary">
                <span>{{ t('insurance.rating.result.basePremium') }}: <strong>{{ breakdown()!.basePremium }}</strong></span>
                <span>{{ t('insurance.rating.result.loadings') }}: <strong>{{ breakdown()!.totalLoadings }}</strong></span>
                <span>{{ t('insurance.rating.result.discounts') }}: <strong>{{ breakdown()!.totalDiscounts }}</strong></span>
                <span>{{ t('insurance.rating.result.taxes') }}: <strong>{{ breakdown()!.totalTaxesAndFees }}</strong></span>
                <span>{{ t('insurance.rating.result.total') }}: <strong>{{ breakdown()!.totalPremium }}</strong></span>
              </div>
            }
          }
        </section>
      }

      <ng-template #rulesTable let-kind="kind">
        <div class="lines-header">
          <h4>{{ t(ruleSectionTitle(kind)) }}</h4>
          <div class="lines-actions">
            <ain-info-hint
              [titleKey]="ruleHelpTitleKey(kind)"
              [descriptionKey]="ruleHelpDescriptionKey(kind)"
              [bulletKeys]="[
                'insurance.rating.help.rulesCommon.bullets.type',
                'insurance.rating.help.rulesCommon.bullets.factor',
                'insurance.rating.help.rulesCommon.bullets.source'
              ]" />
            <button pButton type="button" [label]="t('common.add')" (click)="openRuleCreate(kind)"></button>
          </div>
        </div>
        <p-table [value]="ruleRows(kind)">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('insurance.rating.fields.ruleCode') }}</th>
              <th>{{ t('insurance.rating.fields.ruleType') }}</th>
              <th>{{ t('insurance.rating.fields.ruleValue') }}</th>
              <th>{{ t('insurance.rating.fields.factorKey') }}</th>
              <th>{{ t('insurance.rating.fields.coverageCode') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row let-i="rowIndex">
            <tr>
              <td>{{ row.code }}</td>
              <td>{{ row.type }}</td>
              <td>{{ row.value }}</td>
              <td>{{ row.factorKey }}</td>
              <td>{{ row.coverageCode || t('insurance.rating.fields.allCoverages') }}</td>
              <td class="actions-cell">
                <button pButton type="button" text="true" [label]="t('common.edit')" (click)="openRuleEdit(kind, i)"></button>
                <button pButton type="button" text="true" severity="danger" [label]="t('common.delete')" (click)="removeRule(kind, i)"></button>
              </td>
            </tr>
          </ng-template>
        </p-table>
      </ng-template>

      <p-dialog [header]="coverageDialogTitle()" [modal]="true" [(visible)]="coverageDialogVisible" [style]="{ width: '640px' }">
        <div class="grid">
          <label>
            <span>{{ t('insurance.rating.fields.coverageCode') }}</span>
            <p-select [options]="coverageOptions()" optionLabel="label" optionValue="value" [(ngModel)]="coverageDraft.coverageCode" (ngModelChange)="onCoverageDraftCodeChanged($event)" appendTo="body" />
          </label>
          <label>
            <span>{{ t('insurance.rating.fields.baseRatePercent') }}</span>
            <p-inputNumber [(ngModel)]="coverageDraft.baseRatePercent" [min]="0" [maxFractionDigits]="6" />
          </label>
          <label>
            <span>{{ t('insurance.rating.fields.minPremium') }}</span>
            <p-inputNumber [(ngModel)]="coverageDraft.minPremium" [min]="0" />
          </label>
          <label>
            <span>{{ t('insurance.rating.fields.maxPremium') }}</span>
            <p-inputNumber [(ngModel)]="coverageDraft.maxPremium" [min]="0" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="coverageDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="saveCoverageDialog()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="ruleDialogTitle()" [modal]="true" [(visible)]="ruleDialogVisible" [style]="{ width: '760px' }">
        <div class="grid">
          <label>
            <span>{{ t('insurance.rating.fields.ruleCode') }}</span>
            <input pInputText [(ngModel)]="ruleDraft.code" />
          </label>
          <label>
            <span>{{ t('insurance.rating.fields.ruleType') }}</span>
            <p-select [options]="ruleTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="ruleDraft.type" appendTo="body" />
          </label>
          <label>
            <span>{{ t('insurance.rating.fields.ruleValue') }}</span>
            <p-inputNumber [(ngModel)]="ruleDraft.value" [maxFractionDigits]="6" />
          </label>
          <label>
            <span>{{ t('insurance.rating.fields.factorKey') }}</span>
            <p-select [options]="factorKeyOptions()" optionLabel="label" optionValue="value" [(ngModel)]="ruleDraft.factorKey" (ngModelChange)="onRuleDialogFactorKeyChanged($event)" appendTo="body" />
          </label>
          @if (ruleDraft.factorKey === 'customer') {
            <label>
              <span>{{ t('insurance.rating.fields.customerFactor') }}</span>
              <ain-customer-selector [(ngModel)]="ruleDraft.factorRefId" />
            </label>
          } @else if (ruleDraft.factorKey === 'channel') {
            <label>
              <span>{{ t('insurance.rating.fields.channelFactor') }}</span>
              <p-select [options]="channelFactorOptions()" optionLabel="label" optionValue="value" [(ngModel)]="ruleDraft.factorRefId" [showClear]="true" appendTo="body" />
            </label>
          }
          <label>
            <span>{{ t('insurance.rating.fields.factorMin') }}</span>
            <p-inputNumber [(ngModel)]="ruleDraft.factorMin" [disabled]="usesMasterFactor(ruleDraft.factorKey)" [maxFractionDigits]="6" />
          </label>
          <label>
            <span>{{ t('insurance.rating.fields.factorMax') }}</span>
            <p-inputNumber [(ngModel)]="ruleDraft.factorMax" [disabled]="usesMasterFactor(ruleDraft.factorKey)" [maxFractionDigits]="6" />
          </label>
          <label>
            <span>{{ t('insurance.rating.fields.coverageCode') }}</span>
            <p-select [options]="ruleCoverageOptions()" optionLabel="label" optionValue="value" [(ngModel)]="ruleDraft.coverageCode" appendTo="body" />
          </label>
          <label>
            <span>{{ t('insurance.rating.fields.sourceRuleId') }}</span>
            <input pInputText [(ngModel)]="ruleDraft.sourceRuleId" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="ruleDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="saveRuleDialog()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3, .card h4 { margin: 0; }
    .grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}
    label{display:grid;gap:6px}
    .hint{color:#0f766e;margin:0}
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .section-title { display:flex;align-items:center;gap:8px }
    .lines-header { display: flex; justify-content: space-between; align-items: center; }
    .lines-actions { display:flex;align-items:center;gap:8px }
    .actions-cell{display:flex;gap:6px;justify-content:flex-end}
    .actions-row{display:flex;gap:8px;flex-wrap:wrap}
    .summary{display:flex;gap:12px;flex-wrap:wrap;font-size:14px}
    .row{border-top:1px dashed #e5e7eb;padding-top:8px;margin-top:8px}
    @media (max-width: 900px){ .grid{grid-template-columns:1fr} }
  `]
})
export class InsuranceRatingPlanDetailComponent implements OnInit {
  private readonly api = inject(InsuranceApiService);
  private readonly salesApi = inject(SalesApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly feedback = signal('');
  readonly activeTab = signal<DetailTab>('coverageRates');
  readonly planId = signal<string | null>(null);
  readonly isCreateMode = signal(true);

  readonly products = signal<InsuranceProductListItem[]>([]);
  readonly versions = signal<InsuranceProductVersionListItem[]>([]);
  readonly plans = signal<InsuranceRatingPlan[]>([]);
  readonly channels = signal<SalesChannelRecord[]>([]);
  readonly coverageDefinitions = signal<InsuranceCoverageItem[]>([]);

  readonly breakdown = signal<InsurancePremiumBreakdown | null>(null);
  readonly simulationResults = signal<Array<{ scenarioKey: string; totalPremium: number; pricingHash: string }>>([]);

  selectedProductId: string | null = null;
  selectedVersionId: string | null = null;
  sumInsured = 100000000;
  factorValue = 1;
  testFactors: TestFactorRow[] = [];

  readonly modeOptions = [
    { label: 'Table', value: 'Table' },
    { label: 'Formula', value: 'Formula' },
    { label: 'Hybrid', value: 'Hybrid' },
    { label: 'ExternalAdapter', value: 'ExternalAdapter' }
  ];
  readonly ruleTypeOptions = [
    { label: 'Percent', value: 'Percent' },
    { label: 'Fixed', value: 'Fixed' }
  ];

  planForm: RatingPlanForm = this.emptyPlanForm();
  rateTableForm: RateTableForm = this.emptyRateTableForm();

  readonly coverageDialogVisible = signal(false);
  readonly coverageDialogMode = signal<'create' | 'edit'>('create');
  private coverageDialogIndex = -1;
  coverageDraft: CoverageRateFormRow = this.emptyCoverageRow();

  readonly ruleDialogVisible = signal(false);
  readonly ruleDialogMode = signal<'create' | 'edit'>('create');
  private ruleDialogIndex = -1;
  private ruleDialogKind: RuleKind = 'loadings';
  ruleDraft: RuleFormRow = this.emptyRuleRow();

  ngOnInit(): void {
    this.loading.set(true);
    this.api.listProducts().subscribe({
      next: products => {
        this.products.set(products);
        this.api.listRatingPlans().subscribe({
          next: plans => {
            this.plans.set(plans);
            this.salesApi.listChannels().subscribe({
              next: channels => {
                this.channels.set(channels.filter(item => item.isActive));
                this.hydrateFromRoute();
              },
              error: () => {
                this.channels.set([]);
                this.hydrateFromRoute();
              }
            });
          },
          error: () => {
            this.plans.set([]);
            this.hydrateFromRoute();
          }
        });
      },
      error: () => {
        this.products.set([]);
        this.plans.set([]);
        this.channels.set([]);
        this.hydrateFromRoute();
      }
    });
  }

  pageTitle(): string {
    return this.isCreateMode() ? this.t('insurance.rating.planEditor.create') : this.t('insurance.rating.planEditor.edit');
  }

  canSaveGeneral(): boolean {
    return !!this.selectedVersionId
      && !!this.planForm.code.trim()
      && !!this.planForm.name.trim()
      && !!this.planForm.currencyCode.trim()
      && !!this.planForm.calculationMode.trim()
      && !!this.currentLegalEntityId();
  }

  productOptions(): Array<{ label: string; value: string }> {
    return this.products().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  versionOptions(): Array<{ label: string; value: string }> {
    return this.versions().map(item => ({ value: item.id, label: `v${item.versionNumber} (${item.status})` }));
  }

  coverageOptions(): Array<{ label: string; value: string }> {
    return this.coverageDefinitions().map(item => ({ value: item.code, label: `${item.code} - ${item.name}` }));
  }

  ruleCoverageOptions(): Array<{ label: string; value: string }> {
    return [{ value: '', label: this.t('insurance.rating.fields.allCoverages') }, ...this.coverageOptions()];
  }

  channelFactorOptions(): Array<{ label: string; value: string }> {
    return this.channels().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  factorKeyOptions(): Array<{ label: string; value: string }> {
    return [
      { label: this.t('insurance.rating.factorKeys.none'), value: '' },
      { label: this.t('insurance.rating.factorKeys.age'), value: 'age' },
      { label: this.t('insurance.rating.factorKeys.sumInsuredBand'), value: 'sumInsuredBand' },
      { label: this.t('insurance.rating.factorKeys.regionRisk'), value: 'regionRisk' },
      { label: this.t('insurance.rating.factorKeys.claimHistoryScore'), value: 'claimHistoryScore' },
      { label: this.t('insurance.rating.factorKeys.paymentFrequency'), value: 'paymentFrequency' },
      { label: this.t('insurance.rating.factorKeys.customer'), value: 'customer' },
      { label: this.t('insurance.rating.factorKeys.channel'), value: 'channel' }
    ];
  }

  onProductChanged(productId: string | null): void {
    this.selectedProductId = productId;
    this.selectedVersionId = null;
    this.versions.set([]);
    this.coverageDefinitions.set([]);
    if (!productId) {
      return;
    }

    this.api.getProduct(productId).subscribe({
      next: product => {
        this.versions.set(product?.versions ?? []);
        this.selectedVersionId = product?.versions[0]?.id ?? null;
        this.loadCoverageDefinitions();
      },
      error: () => {
        this.versions.set([]);
        this.selectedVersionId = null;
        this.coverageDefinitions.set([]);
      }
    });
  }

  onVersionChanged(versionId: string | null): void {
    this.selectedVersionId = versionId;
    this.loadCoverageDefinitions();
  }

  saveGeneral(): void {
    if (!this.canSaveGeneral() || !this.selectedVersionId) {
      return;
    }
    const legalEntityId = this.currentLegalEntityId();
    if (!legalEntityId) {
      return;
    }
    this.saving.set(true);
    this.api.upsertRatingPlan({
      code: this.planForm.code.trim(),
      name: this.planForm.name.trim(),
      productVersionId: this.selectedVersionId,
      legalEntityId,
      currencyCode: this.planForm.currencyCode.trim(),
      calculationMode: this.planForm.calculationMode.trim(),
      externalAdapterKey: this.planForm.externalAdapterKey?.trim() || null,
      rateTableJson: this.serializeRateTableForm(this.rateTableForm)
    }).subscribe({
      next: saved => {
        this.planId.set(saved.id);
        this.isCreateMode.set(false);
        this.feedback.set(this.t('insurance.rating.messages.saved'));
        this.saving.set(false);
        if (!this.route.snapshot.paramMap.get('ratingPlanId')) {
          void this.router.navigate(['/insurance/rating', saved.id]);
        }
      },
      error: () => {
        this.feedback.set(this.t('insurance.rating.messages.saveFailed'));
        this.saving.set(false);
      }
    });
  }

  ruleRows(kind: RuleKind): RuleFormRow[] {
    return this.rateTableForm[kind];
  }

  ruleSectionTitle(kind: RuleKind): string {
    return `insurance.rating.sections.${kind}`;
  }

  ruleHelpTitleKey(kind: RuleKind): string {
    return `insurance.rating.help.${kind}.title`;
  }

  ruleHelpDescriptionKey(kind: RuleKind): string {
    return `insurance.rating.help.${kind}.description`;
  }

  openCoverageCreate(): void {
    this.coverageDialogMode.set('create');
    this.coverageDialogIndex = -1;
    this.coverageDraft = this.emptyCoverageRow();
    this.coverageDraft.coverageCode = this.coverageDefinitions()[0]?.code ?? '';
    this.onCoverageDraftCodeChanged(this.coverageDraft.coverageCode);
    this.coverageDialogVisible.set(true);
  }

  openCoverageEdit(index: number): void {
    const row = this.rateTableForm.coverageRates[index];
    if (!row) return;
    this.coverageDialogMode.set('edit');
    this.coverageDialogIndex = index;
    this.coverageDraft = { ...row };
    this.coverageDialogVisible.set(true);
  }

  removeCoverage(index: number): void {
    this.rateTableForm.coverageRates.splice(index, 1);
  }

  onCoverageDraftCodeChanged(code: string | null): void {
    this.coverageDraft.coverageCode = code ?? '';
    const coverage = this.coverageDefinitions().find(item => item.code === this.coverageDraft.coverageCode);
    if (!coverage) return;
    if (this.coverageDraft.minPremium == null && coverage.minSumInsured != null) this.coverageDraft.minPremium = coverage.minSumInsured;
    if (this.coverageDraft.maxPremium == null && coverage.maxSumInsured != null) this.coverageDraft.maxPremium = coverage.maxSumInsured;
    if (this.sumInsured <= 0 && coverage.defaultSumInsured != null) this.sumInsured = coverage.defaultSumInsured;
  }

  saveCoverageDialog(): void {
    if (!this.coverageDraft.coverageCode.trim()) {
      return;
    }
    if (this.coverageDialogMode() === 'create') {
      this.rateTableForm.coverageRates.push({ ...this.coverageDraft });
    } else if (this.coverageDialogIndex >= 0) {
      this.rateTableForm.coverageRates[this.coverageDialogIndex] = { ...this.coverageDraft };
    }
    this.coverageDialogVisible.set(false);
  }

  coverageDialogTitle(): string {
    return this.coverageDialogMode() === 'create' ? this.t('common.add') : this.t('common.edit');
  }

  openRuleCreate(kind: RuleKind): void {
    this.ruleDialogKind = kind;
    this.ruleDialogMode.set('create');
    this.ruleDialogIndex = -1;
    this.ruleDraft = this.emptyRuleRow();
    this.ruleDialogVisible.set(true);
  }

  openRuleEdit(kind: RuleKind, index: number): void {
    const rows = this.ruleRows(kind);
    const row = rows[index];
    if (!row) return;
    this.ruleDialogKind = kind;
    this.ruleDialogMode.set('edit');
    this.ruleDialogIndex = index;
    this.ruleDraft = { ...row };
    this.ruleDialogVisible.set(true);
  }

  removeRule(kind: RuleKind, index: number): void {
    this.ruleRows(kind).splice(index, 1);
  }

  onRuleDialogFactorKeyChanged(factorKey: string | null): void {
    this.ruleDraft.factorKey = factorKey ?? '';
    if (!this.usesMasterFactor(this.ruleDraft.factorKey)) {
      this.ruleDraft.factorRefId = null;
      return;
    }
    this.ruleDraft.factorMin = null;
    this.ruleDraft.factorMax = null;
  }

  usesMasterFactor(factorKey: string): boolean {
    return factorKey === 'customer' || factorKey === 'channel';
  }

  addTestFactor(): void {
    this.testFactors.push(this.emptyTestFactorRow());
  }

  removeTestFactor(index: number): void {
    this.testFactors.splice(index, 1);
  }

  onTestFactorKeyChanged(row: TestFactorRow, factorKey: string | null): void {
    row.factorKey = factorKey ?? '';
    if (this.usesMasterFactor(row.factorKey)) {
      row.factorValue = null;
      if (row.factorKey === 'channel' && !row.factorRefId) {
        row.factorRefId = this.channels()[0]?.id ?? null;
      }
      return;
    }

    row.factorRefId = null;
    if (row.factorValue == null) {
      row.factorValue = 1;
    }
  }

  saveRuleDialog(): void {
    if (!this.ruleDraft.code.trim()) {
      return;
    }
    if (this.usesMasterFactor(this.ruleDraft.factorKey) && !this.ruleDraft.factorRefId) {
      return;
    }
    if (this.ruleDialogMode() === 'create') {
      this.ruleRows(this.ruleDialogKind).push({ ...this.ruleDraft });
    } else if (this.ruleDialogIndex >= 0) {
      this.ruleRows(this.ruleDialogKind)[this.ruleDialogIndex] = { ...this.ruleDraft };
    }
    this.ruleDialogVisible.set(false);
  }

  ruleDialogTitle(): string {
    return this.ruleDialogMode() === 'create' ? this.t('common.add') : this.t('common.edit');
  }

  canCalculate(): boolean {
    return !!this.selectedVersionId && this.sumInsured > 0;
  }

  calculate(): void {
    if (!this.selectedVersionId) return;
    this.api.calculatePremium({
      productVersionId: this.selectedVersionId,
      ratingPlanId: this.planId(),
      currencyCode: this.planForm.currencyCode || 'IRR',
      factors: this.buildRuntimeFactors(this.factorValue || 1),
      coverages: this.buildCoverageSelections()
    }).subscribe({
      next: result => this.breakdown.set(result),
      error: () => this.breakdown.set(null)
    });
  }

  reprice(): void {
    if (!this.selectedVersionId) return;
    this.api.repricePremium({
      quoteId: crypto.randomUUID(),
      calculation: {
        productVersionId: this.selectedVersionId,
        ratingPlanId: this.planId(),
        currencyCode: this.planForm.currencyCode || 'IRR',
        factors: this.buildRuntimeFactors(this.factorValue || 1),
        coverages: this.buildCoverageSelections()
      }
    }).subscribe({
      next: result => this.breakdown.set(result),
      error: () => this.breakdown.set(null)
    });
  }

  simulate(): void {
    const id = this.planId();
    if (!id || !this.selectedVersionId) return;
    this.api.simulateRatingPlan(id, {
      productVersionId: this.selectedVersionId,
      currencyCode: this.planForm.currencyCode || 'IRR',
      scenarios: [
        { scenarioKey: 'base', factors: this.buildRuntimeFactors(1), coverages: this.buildCoverageSelections() },
        { scenarioKey: 'highFactor', factors: this.buildRuntimeFactors(Math.max(this.factorValue, 1.5)), coverages: this.buildCoverageSelections() }
      ]
    }).subscribe({
      next: result => this.simulationResults.set(result.results),
      error: () => this.simulationResults.set([])
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private hydrateFromRoute(): void {
    const ratingPlanId = this.route.snapshot.paramMap.get('ratingPlanId');
    if (!ratingPlanId) {
      this.isCreateMode.set(true);
      this.planId.set(null);
      this.selectedProductId = this.products()[0]?.id ?? null;
      this.onProductChanged(this.selectedProductId);
      this.loading.set(false);
      return;
    }

    this.isCreateMode.set(false);
    const plan = this.plans().find(item => item.id === ratingPlanId);
    if (!plan) {
      this.feedback.set(this.t('insurance.states.notFoundMessage'));
      this.loading.set(false);
      return;
    }

    this.planId.set(plan.id);
    this.planForm = {
      code: plan.code,
      name: plan.name,
      currencyCode: plan.currencyCode,
      calculationMode: plan.calculationMode,
      externalAdapterKey: plan.externalAdapterKey ?? ''
    };
    this.rateTableForm = this.parseRateTableJson(plan.rateTableJson);
    this.resolveProductByVersion(plan.productVersionId, foundProductId => {
      this.selectedProductId = foundProductId;
      this.onProductChanged(this.selectedProductId);
      this.selectedVersionId = plan.productVersionId;
      this.loadCoverageDefinitions();
      this.loading.set(false);
    });
  }

  private resolveProductByVersion(versionId: string, onResolved: (productId: string | null) => void): void {
    const products = this.products();
    if (products.length === 0) {
      onResolved(null);
      return;
    }

    let index = 0;
    const tryNext = () => {
      if (index >= products.length) {
        onResolved(null);
        return;
      }
      const productId = products[index++].id;
      this.api.getProduct(productId).subscribe({
        next: detail => {
          if (detail?.versions.some(item => item.id === versionId)) {
            onResolved(productId);
          } else {
            tryNext();
          }
        },
        error: () => tryNext()
      });
    };
    tryNext();
  }

  private currentLegalEntityId(): string | null {
    return this.products().find(item => item.id === this.selectedProductId)?.legalEntityId ?? null;
  }

  private loadCoverageDefinitions(): void {
    if (!this.selectedProductId || !this.selectedVersionId) {
      this.coverageDefinitions.set([]);
      return;
    }
    this.api.listVersionCoverages(this.selectedProductId, this.selectedVersionId).subscribe({
      next: rows => this.coverageDefinitions.set(rows),
      error: () => this.coverageDefinitions.set([])
    });
  }

  private buildCoverageSelections(): Array<{ coverageCode: string; sumInsured: number; selected: boolean }> {
    const selectedCoverageCodes = this.rateTableForm.coverageRates.map(item => item.coverageCode.trim()).filter(Boolean);
    const fallbackCoverageCode = this.coverageDefinitions()[0]?.code ?? 'BASE';
    const coverageCodes = selectedCoverageCodes.length > 0 ? Array.from(new Set(selectedCoverageCodes)) : [fallbackCoverageCode];
    return coverageCodes.map(code => ({ coverageCode: code, sumInsured: this.sumInsured, selected: true }));
  }

  private buildRuntimeFactors(userFactor: number): Record<string, number> {
    const factors: Record<string, number> = { userFactor };
    this.testFactors.forEach(item => {
      const factorKey = item.factorKey.trim();
      if (!factorKey) {
        return;
      }

      if (this.usesMasterFactor(factorKey)) {
        const factorRefId = item.factorRefId?.trim();
        if (factorRefId) {
          factors[`${factorKey}:${factorRefId}`] = 1;
        }
        return;
      }

      factors[factorKey] = item.factorValue ?? 1;
    });
    return factors;
  }

  private emptyPlanForm(): RatingPlanForm {
    return { code: '', name: '', currencyCode: 'IRR', calculationMode: 'Table', externalAdapterKey: '' };
  }

  private emptyCoverageRow(): CoverageRateFormRow {
    return { coverageCode: '', baseRatePercent: 0.01, minPremium: null, maxPremium: null };
  }

  private emptyRuleRow(): RuleFormRow {
    return { code: '', type: 'Percent', value: 0, factorKey: '', factorRefId: null, factorMin: null, factorMax: null, coverageCode: '', sourceRuleId: '' };
  }

  private emptyTestFactorRow(): TestFactorRow {
    return {
      factorKey: '',
      factorValue: 1,
      factorRefId: null
    };
  }

  private emptyRateTableForm(): RateTableForm {
    return {
      defaultRatePercent: 0.01,
      minPremiumFloor: null,
      coverageRates: [],
      loadings: [],
      discounts: [],
      taxes: [{ code: 'vat', type: 'Percent', value: 0.09, factorKey: '', factorRefId: null, factorMin: null, factorMax: null, coverageCode: '', sourceRuleId: 'rule.tax.vat' }],
      fees: [{ code: 'stamp', type: 'Fixed', value: 0, factorKey: '', factorRefId: null, factorMin: null, factorMax: null, coverageCode: '', sourceRuleId: 'rule.fee.stamp' }]
    };
  }

  private parseRateTableJson(raw: string): RateTableForm {
    try {
      const parsed = JSON.parse(raw || '{}') as Record<string, unknown>;
      return {
        defaultRatePercent: this.asNumber(parsed['defaultRatePercent'], 0.01),
        minPremiumFloor: this.asNullableNumber(parsed['minPremiumFloor']),
        coverageRates: this.parseCoverageRows(parsed['coverageRates']),
        loadings: this.parseRuleRows(parsed['loadings']),
        discounts: this.parseRuleRows(parsed['discounts']),
        taxes: this.parseRuleRows(parsed['taxes']),
        fees: this.parseRuleRows(parsed['fees'])
      };
    } catch {
      return this.emptyRateTableForm();
    }
  }

  private parseCoverageRows(value: unknown): CoverageRateFormRow[] {
    if (!Array.isArray(value)) return [];
    return value.map(item => {
      const row = item as Record<string, unknown>;
      return {
        coverageCode: this.asString(row['coverageCode']),
        baseRatePercent: this.asNumber(row['baseRatePercent'], 0.01),
        minPremium: this.asNullableNumber(row['minPremium']),
        maxPremium: this.asNullableNumber(row['maxPremium'])
      };
    });
  }

  private parseRuleRows(value: unknown): RuleFormRow[] {
    if (!Array.isArray(value)) return [];
    return value.map(item => {
      const row = item as Record<string, unknown>;
      const { factorKey, factorRefId } = this.parseFactorBinding(this.asString(row['factorKey']));
      return {
        code: this.asString(row['code']),
        type: this.asString(row['type']) || 'Percent',
        value: this.asNumber(row['value'], 0),
        factorKey,
        factorRefId,
        factorMin: this.asNullableNumber(row['factorMin']),
        factorMax: this.asNullableNumber(row['factorMax']),
        coverageCode: this.asString(row['coverageCode']),
        sourceRuleId: this.asString(row['sourceRuleId'])
      };
    });
  }

  private serializeRateTableForm(form: RateTableForm): string {
    const payload = {
      defaultRatePercent: this.asNumber(form.defaultRatePercent, 0.01),
      minPremiumFloor: form.minPremiumFloor,
      coverageRates: form.coverageRates
        .filter(item => item.coverageCode.trim().length > 0)
        .map(item => ({
          coverageCode: item.coverageCode.trim(),
          baseRatePercent: this.asNumber(item.baseRatePercent, 0.01),
          minPremium: item.minPremium,
          maxPremium: item.maxPremium
        })),
      loadings: this.serializeRuleRows(form.loadings),
      discounts: this.serializeRuleRows(form.discounts),
      taxes: this.serializeRuleRows(form.taxes),
      fees: this.serializeRuleRows(form.fees)
    };
    return JSON.stringify(payload);
  }

  private serializeRuleRows(rows: RuleFormRow[]): Array<Record<string, unknown>> {
    return rows
      .filter(item => item.code.trim().length > 0)
      .map(item => ({
        code: item.code.trim(),
        type: item.type || 'Percent',
        value: this.asNumber(item.value, 0),
        factorKey: this.serializeFactorKey(item),
        factorMin: this.usesMasterFactor(item.factorKey) ? null : item.factorMin,
        factorMax: this.usesMasterFactor(item.factorKey) ? null : item.factorMax,
        coverageCode: item.coverageCode?.trim() || null,
        sourceRuleId: item.sourceRuleId?.trim() || null
      }));
  }

  private parseFactorBinding(rawFactorKey: string): { factorKey: string; factorRefId: string | null } {
    const normalized = rawFactorKey.trim();
    if (!normalized) return { factorKey: '', factorRefId: null };
    if (normalized.startsWith('channel:')) return { factorKey: 'channel', factorRefId: normalized.slice('channel:'.length) || null };
    if (normalized.startsWith('customer:')) return { factorKey: 'customer', factorRefId: normalized.slice('customer:'.length) || null };
    if (normalized.startsWith('user:')) return { factorKey: 'customer', factorRefId: normalized.slice('user:'.length) || null };
    return { factorKey: normalized, factorRefId: null };
  }

  private serializeFactorKey(row: RuleFormRow): string | null {
    const factorKey = row.factorKey?.trim() || '';
    if (!factorKey) return null;
    if (this.usesMasterFactor(factorKey)) {
      const factorRefId = row.factorRefId?.trim();
      return factorRefId ? `${factorKey}:${factorRefId}` : null;
    }
    return factorKey;
  }

  private asNumber(value: unknown, fallback: number): number {
    return typeof value === 'number' && !Number.isNaN(value) ? value : fallback;
  }

  private asNullableNumber(value: unknown): number | null {
    return typeof value === 'number' && !Number.isNaN(value) ? value : null;
  }

  private asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }
}

type RatingPlanForm = {
  code: string;
  name: string;
  currencyCode: string;
  calculationMode: string;
  externalAdapterKey: string;
};

type CoverageRateFormRow = {
  coverageCode: string;
  baseRatePercent: number;
  minPremium: number | null;
  maxPremium: number | null;
};

type RuleFormRow = {
  code: string;
  type: string;
  value: number;
  factorKey: string;
  factorRefId: string | null;
  factorMin: number | null;
  factorMax: number | null;
  coverageCode: string;
  sourceRuleId: string;
};

type RateTableForm = {
  defaultRatePercent: number;
  minPremiumFloor: number | null;
  coverageRates: CoverageRateFormRow[];
  loadings: RuleFormRow[];
  discounts: RuleFormRow[];
  taxes: RuleFormRow[];
  fees: RuleFormRow[];
};

type TestFactorRow = {
  factorKey: string;
  factorValue: number | null;
  factorRefId: string | null;
};
